import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthLayout from '../../Layout/Auth/AuthLayout';
import { validateOtp } from '../../../utils/validation';
import { ERROR_CODES } from '../../../utils/errorMessages';
import OtpVerificationCard from './OtpVerificationCard';
import { CONSTS, TOAST_CONFIG, TOAST_IDS } from '../../../const';
import OtpVerificationContent from './OtpVerificationContent';
import useAuthTokens from '../../../utils/hooks/useAuthTokens';
import {
  getErrMsgFromResp,
  handleErrorToast,
} from '../../../utils/errorHandler';
import { handleLoginSuccess } from '../../../pages/Login/Login';
import { graphqlClient } from '../../../utils/client/graphql';
import {
  RESEND_OTP_MUTATION,
  VERIFY_OTP_MUTATION,
} from '../../../graphql/queries';

const OtpVerification = (props) => {
  const {
    isLoginFlow,
    isForgotFlow,
    sourceTypeParam,
    isOnboardFlow,
    className,
    onboardingVerification,
  } = props;
  const { otpVerificationStep = undefined, handleNextButton = undefined } =
    onboardingVerification || {};

  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setTokens } = useAuthTokens();
  const { email, password, otpKey, mobile, resendTime, sourceState, user } =
    location.state || {};
  const [sourceType, setSourceType] = useState(sourceState || sourceTypeParam);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(resendTime);
  const [maxResendReached, setMaxResendReached] = useState(false);
  const [verifyDisabled, setVerifyDisabled] = useState(false);
  console.log('timeRemaining', timeRemaining);
  // useEffect(() => {
  //   // Start initial timer
  //   startResendTimer();
  // }, []);

  const resetPageValues = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    setResendDisabled(true);
    setTimeRemaining(resendTime);
    setMaxResendReached(false);
    setVerifyDisabled(false);
    setSourceType(sourceState || sourceTypeParam);
  };

  useEffect(() => {
    let timer;
    if (timeRemaining > 0 && resendDisabled && !maxResendReached) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0 && !maxResendReached) {
      setResendDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeRemaining, resendDisabled, maxResendReached]);

  // Re-sync local state when navigating between OTP rounds (e.g. EMAIL -> MOBILE)
  // on the same route. The component does not remount, so useState initializers
  // are not re-evaluated; we must reset based on the new location.state.
  useEffect(() => {
    setSourceType(location.state?.sourceState || sourceTypeParam);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setResendDisabled(true);
    setTimeRemaining(location.state?.resendTime);
    setMaxResendReached(false);
    setVerifyDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.otpKey, location.state?.sourceState]);

  const startResendTimer = () => {
    setResendDisabled(true);
    setTimeRemaining(resendTime);
  };

  const handleVerifyOtp = async (source) => {
    setLoading(true);
    const otpValidation = validateOtp(otp);
    if (!otpValidation.isValid) {
      setError(otpValidation.error);
      return;
    }

    try {
      const otpValue = otp.join('');

      const input = {
        email: source == 'MOBILE' && isLoginFlow ? user : email,
        otp: otpValue,
        otpKey,
      };

      const response = await graphqlClient.request(VERIFY_OTP_MUTATION, {
        input,
      });

      const verify = response?.verifyOtp;
      const status = verify?.status;
      const statusCode = verify?.statusCode;
      const data = verify?.data;
      const message = verify?.message;
      const errorCode = verify?.errorCode;

      if (status === CONSTS.OK) {
        if (isLoginFlow) {
          if (errorCode == 'OTP_REQUIRED') {
            if (data?.sourceType == 'MOBILE') {
              navigate('/login', {
                state: {
                  screen: 'otp',
                  mobile: data?.source,
                  otpKey: data.otpKey,

                  resendTime: data.resendReadyTime,
                  sourceState: data?.sourceType,
                  user: data?.user,
                },
              });
              return;
            } else if (data?.sourceType == 'EMAIL') {
              navigate('/login', {
                state: {
                  screen: 'otp',
                  email: data?.source,
                  otpKey: data.otpKey,
                  resendTime: data.resendReadyTime,
                  sourceState: data?.sourceType,
                },
              });
              return;
            }
          }
          if (errorCode == 'SET_PASSWORD') {
            navigate('/signup', {
              state: {
                screen: 'set-password',
                email: data?.source,
                verificationkey: data.otpKey,
              },
            });
            return;
          }

          handleLoginSuccess(data);
        } else if (isForgotFlow) {
          // Forgot password flow: Go to reset password
          navigate('/forgot-password', {
            state: {
              screen: 'reset-password',
              email: email,
              verificationkey: data.otpKey,
            },
          });
        } else {
          // Signup flow: Go to set password
          navigate('/signup', {
            state: {
              screen: 'set-password',
              email: email,
              verificationkey: data.otpKey,
            },
          });
        }
        setTimeRemaining(data.resendAttemptsCount);
      } else {
        switch (errorCode) {
          case ERROR_CODES.TRY_AFTER_SOME_TIME:
            setVerifyDisabled(true);
            setResendDisabled(true);
            setTimeout(() => setVerifyDisabled(false), 300000); // 5 minutes
            break;

          case ERROR_CODES.MAX_OTP_LIMIT_EXCEEDED:
            setMaxResendReached(true);
            setResendDisabled(true);
            break;

          default:
            if (status === CONSTS.VER || status === CONSTS.ERR) {
              throw response;
            }
        }
      }
    } catch (err) {
      setError(getErrMsgFromResp(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await graphqlClient.request(RESEND_OTP_MUTATION, {
        input: {
          email: email || user,
          otpKey,
        },
      });

      const resend = response?.resendOtp;
      const status = resend?.status;
      const errorCode = resend?.errorCode;
      const data = resend?.data;

      if (status === CONSTS.OK) {
        startResendTimer();

        if (data?.resendAttemptsCount === 1) {
          handleErrorToast('Only One attempt left!');
        } else {
          toast.success('OTP resent Successfully!', {
            ...TOAST_CONFIG,
            toastId: TOAST_IDS.SUCCESS,
          });
        }
      } else {
        switch (errorCode) {
          case ERROR_CODES.MAX_RESEND_REACHED:
            setMaxResendReached(true);
            break;

          case ERROR_CODES.MAX_OTP_LIMIT_EXCEEDED:
            setMaxResendReached(true);
            break;

          default:
            setResendDisabled(true);
            throw response;
        }
        if (status === CONSTS.VER || status === CONSTS.ERR) {
          throw response;
        }
      }
    } catch (err) {
      setError(getErrMsgFromResp(err));
    }
  };
  useEffect(() => {
    handleErrorToast(error);
    setTimeout(() => {
      setError('');
    }, 500);
  }, [error]);

  return (
    <AuthLayout leftContent={<OtpVerificationContent />}>
      <OtpVerificationCard
        loading={loading}
        email={email}
        mobile={mobile}
        sourceType={sourceType}
        otp={otp}
        setOtp={setOtp}
        handleVerifyOtp={handleVerifyOtp}
        handleResendOtp={handleResendOtp}
        error={error}
        isLoginFlow={isLoginFlow}
        isForgotFlow={isForgotFlow}
        resendDisabled={resendDisabled || maxResendReached}
        timeRemaining={timeRemaining}
        verifyDisabled={verifyDisabled}
      />
    </AuthLayout>
  );
};

export default OtpVerification;
